import { useRef } from 'react';
import TabOptionWithSelect from 'src/components/primitives/Tabs/TabOptionWithSelect';
import TabIndicator from 'src/components/primitives/Tabs/TabIndicator';
import { TabsProps } from 'src/components/primitives/Tabs/types';
import Tag from 'src/components/primitives/Tag/Tag';
import { cm } from 'src/utils/classNames';
import { Z_INDEX } from 'src/constants/zIndex';
import { typedMemo } from 'src/utils/react';
import useTabOptionsForScreenSize from 'src/components/primitives/Tabs/hooks/useTabOptionsForScreenSize';

const TAB_STYLE = {
	zIndex: Z_INDEX.tab,
};

function Tabs<T extends string, S extends string>({
	size = 'md',
	variant = 'primary',
	indicatorTrack,
	activeTab,
	onTabChange,
	options,
	className,
	dataTestId,
	tabTestIdPrefix,
}: TabsProps<T, S>) {
	const tabOptionsForScreenSize = useTabOptionsForScreenSize({
		options,
		onTabChange,
		activeTab,
	});

	// `tabListRef` and `activeIndex` are used by the animated indicator to derive
	// its styles (e.g. position, width, etc.).
	const tabListRef = useRef<HTMLDivElement>(null);
	const activeIndex = tabOptionsForScreenSize.findIndex(
		(option) => option.value === activeTab
	);

	const variantClassName = {
		primary: 'gap-x-md',
		secondary: 'gap-x-sm',
	}[variant];

	const sizeClassName = {
		sm: 'typo-t4 py-xs',
		md: 'typo-t3 py-sm',
		lg: 'typo-t2 py-md',
	}[size];

	return (
		<div
			// `relative` used here to contain the indicator.
			className={cm(
				'relative isolate flex items-center',
				variantClassName,
				className
			)}
			ref={tabListRef}
			data-testid={dataTestId}
		>
			{tabOptionsForScreenSize.map(
				({
					value,
					label,
					tag,
					selectOptions,
					selectedValue,
					onSelectedValueChange,
					className: optionClassName,
					disabled,
				}) => {
					const className = cm(
						'flex items-center gap-x-xs transition-colors',
						'w-full justify-center border-transparent whitespace-nowrap',
						['px-md', sizeClassName],
						!disabled
							? 'text-text-secondary hover:text-text-default data-[active=true]:text-text-default'
							: 'text-text-disabled',
						optionClassName
					);

					if (selectOptions) {
						return (
							<div
								// Using `activeTab` here ensures the select component resets
								// when the tab is changed, which is necessary so it doesn't
								// retain any previously selected value.
								key={`slider-${activeTab}`}
								data-active={value === activeTab}
								className={className}
								style={TAB_STYLE}
								data-testid={
									tabTestIdPrefix ? `${tabTestIdPrefix}-${value}` : undefined
								}
							>
								<TabOptionWithSelect
									selectOptions={selectOptions}
									selectedValue={selectedValue}
									onSelectedValueChange={onSelectedValueChange}
									label={label}
									disabled={disabled}
								/>
							</div>
						);
					}

					return (
						<button
							key={value}
							className={className}
							data-active={value === activeTab}
							onClick={() => onTabChange(value)}
							disabled={disabled}
							style={TAB_STYLE}
							data-testid={
								tabTestIdPrefix ? `${tabTestIdPrefix}-${value}` : undefined
							}
						>
							{label}
							{tag && <Tag {...tag} />}
						</button>
					);
				}
			)}
			<TabIndicator
				activeIndex={activeIndex}
				variant={variant}
				size={size}
				ref={tabListRef}
				indicatorTrack={!!indicatorTrack}
			/>
		</div>
	);
}

export default typedMemo(Tabs);
